import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { userAPI } from '../../actions/imsServer';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ContentArea from '../../components/contentArea';
import FormLabel from '../../components/formItem/formLabel';
import CommonCard from '../../components/card/CommonCard';
import { PhotoEmpty, Memo, Point, Next } from '../../assets/images';
import LevelProgressCard from '../../components/levelProgressCard';
import { useDispatch } from 'react-redux';
import { setAuth } from '../../redux/infoSlice';
import { isWebView } from '../../utils/base';
import { membershipAPI } from '../../actions/imsServer';
import appVersionManager from '../../utils/appVersion';
import Button from '../../components/button';
import InputField from '../../components/formItem/inputField';

// 공통 메뉴 아이템 컴포넌트
const MenuItem = ({ title, onClick, hasNotification = false }) => {
    const theme = useTheme();
    const handleClick = () => {
        if (onClick && typeof onClick === 'function') {
            onClick();
        }
    };

    return (
        <CommonCard sx={{
            padding: '5px 15px',
            flexDirection: 'row',
            justifyContent: 'space-between',
            cursor: 'pointer',
        }} onClick={handleClick}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: hasNotification ? '3px' : '0px'
            }}>
                {hasNotification && (
                    <Box sx={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: theme.palette.figma.upRed,
                    }} />
                )}
                <Typography sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.palette.figma.white[80],
                    letterSpacing: '-0.42px'
                }}>
                    {title}
                </Typography>
            </Box>
            <Next />
        </CommonCard>
    );
};

// 공통 메뉴 컨테이너 컴포넌트
const MenuContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%'
};

// 공통 섹션 컨테이너 스타일
const SectionContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%'
};

export const UserMypage = props => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useDispatch();

    // 데스크톱 뷰 감지
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const normalizedDeleteConfirmText = (deleteConfirmText || '').trim().toLowerCase();
    const normalizedDeleteKeyword = t('delete_account').trim().toLowerCase();
    const isDeleteConfirmMatched = normalizedDeleteConfirmText === normalizedDeleteKeyword;
    const [membershipsTotal, setMembershipsTotal] = useState(0);
    const [displayVersion, setDisplayVersion] = useState('');

    const [meInfo, setMeInfo] = useState(() => {
        try {
            const stored = localStorage.getItem('meInfo');
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error('Failed to parse meInfo from localStorage:', error);
            return null;
        }
    });

    const [formData, setFormData] = useState({
        id: 0,
        loginId: '',
        name: '',
        birth: '',
        gender: 0,
        careGrade: '',
        mobile: '010-1234-1234',
        roomInfo: '',
        admissionDate: '',
        createDate: '2025.01.01',
        profilePath: '',
        point: 0,
    });

    useEffect(() => {
        // Load initial user data from localStorage
        const loadUserDataFromStorage = async () => {
            try {
                const meInfoString = localStorage.getItem('meInfo');
                if (!meInfoString) {
                    console.warn('meInfo is not available in localStorage');
                    return;
                }

                const userData = JSON.parse(meInfoString);
                setMeInfo(userData);

                let birthDateForInput = '';
                if (userData.birth && userData.birth.length === 8) {
                    const year = userData.birth.substring(0, 4);
                    const month = userData.birth.substring(4, 6);
                    const day = userData.birth.substring(6, 8);
                    birthDateForInput = `${year}-${month}-${day}`;
                }

                setFormData({
                    id: userData.id || 0,
                    loginId: userData.loginId || '',
                    name: userData.name || '',
                    birth: birthDateForInput,
                    gender: userData.gender !== undefined ? userData.gender : 0,
                    mobile: userData.mobile || '',
                    createDate: userData.createDate || '',
                    profilePath: userData.profilePath || '',
                    careGrade: userData.careGrade,
                    roomInfo: userData.roomInfo || '',
                    admissionDate: userData.admissionDate || '',
                    point: userData.point || 0,
                });

                // Load memberships after userData is loaded
                if (userData?.id) {
                    try {
                        const membershipsResponse = await membershipAPI.getMemberships({
                            userId: userData.id,
                            type: 1,
                            state: 0,
                        });
                        if (membershipsResponse?.data) {
                            setMembershipsTotal(membershipsResponse.data.total);
                        }
                    } catch (error) {
                        console.error('Failed to load memberships:', error);
                    }
                }
            } catch (error) {
                console.error('Failed to load user data from localStorage:', error);
            }
        };

        loadUserDataFromStorage();

        // 앱 버전 정보 가져오기
        let handleAppVersionUpdate = null;

        if (appVersionManager.isInAppEnvironment()) {
            // 즉시 버전 정보 가져오기 시도
            const formattedVersion = appVersionManager.getFormattedVersion();
            if (formattedVersion) {
                setDisplayVersion(formattedVersion);
            } else {
                // 버전 정보가 아직 없으면 대기
                appVersionManager
                    .waitForVersion(3000)
                    .then(() => {
                        const formatted = appVersionManager.getFormattedVersion();
                        if (formatted) {
                            setDisplayVersion(formatted);
                        } else {
                            setDisplayVersion(t('checking_app_version'));
                        }
                    })
                    .catch(() => {
                        setDisplayVersion(t('version_unknown'));
                    });
            }

            // 버전 업데이트 리스너
            handleAppVersionUpdate = () => {
                const formatted = appVersionManager.getFormattedVersion();
                if (formatted) {
                    setDisplayVersion(formatted);
                }
            };

            window.addEventListener('appVersionUpdated', handleAppVersionUpdate);
        } else {
            // 웹 환경에서는 빈 문자열 또는 기본값
            setDisplayVersion('');
        }

        // Cleanup 함수
        return () => {
            if (handleAppVersionUpdate) {
                window.removeEventListener('appVersionUpdated', handleAppVersionUpdate);
            }
        };
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('meInfo');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        dispatch(setAuth({ accessToken: null, refreshToken: null }));

        if (isWebView()) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'logout',
                })
            );
        }
        navigate('/auth/signin');
    };

    const handleDeleteConfirm = async () => {
        setIsLoading(true);
        try {
            await userAPI.unregisterUser({ userId: meInfo.id, type: 1 });

            // Clear local storage
            localStorage.removeItem('meInfo');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');

            // Navigate to login page after a short delay
            setTimeout(() => {
                navigate('/auth/signin');
            }, 1500);
        } catch (error) {
            console.error('Failed to delete account:', error);
            setSnackbar({
                open: true,
                message: t('an_error_occurred'),
                severity: 'error',
            });
        } finally {
            setIsLoading(false);
            setDeleteDialogOpen(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setDeleteConfirmText('');
    };

    // 경험치 상세보기 팝업 관련 함수들
    const handleOpenExpDetail = () => {
        navigate('/users/exp');
    };

    return (
        <ContentArea sx={{
            padding: '0 10px 80px 10px',
            maxWidth: isDesktop ? '720px' : '100%',
            width: '100%',
            margin: '0 auto'
        }}>
            {/* Main Content */}
            <Box sx={{
                flex: 1,
                ...SectionContainerStyle,
                padding: '0 0px 30px 0px',
                maxWidth: isDesktop ? '720px' : '100%',
                width: '100%'
            }}>
                {/* Profile and Point Cards */}
                <Box sx={{
                    display: 'flex',
                    gap: '10px',
                    width: '100%'
                }}>
                    {/* Profile Card */}
                    <CommonCard sx={{ position: 'relative', marginTop: meInfo?.grade >= 50 ? '20px' : '0px' }}>
                        {/* Edit Icon */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                zIndex: 10,
                            }}
                            onClick={(e) => {
                                navigate('/users/mypage-edit');
                            }}>
                            <Memo />
                        </Box>

                        {/* Profile Image Container */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            width: '100%'
                        }}>
                            {/* Profile Image */}
                            <Box sx={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: theme.palette.figma.white[10],
                                position: 'relative'
                            }}>
                                {(formData.profilePath && formData.profilePath.trim() !== '') ? (
                                    <Box
                                        component="img"
                                        src={formData.profilePath}
                                        alt={formData.name}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                        onError={(e) => {
                                            console.error('=== Image Load Error ===');
                                            console.error('URL:', formData.profilePath);
                                            console.error('Error type:', e.type);
                                            console.error('Target:', e.target);
                                        }}
                                    />
                                ) : (
                                    <PhotoEmpty
                                        size={60}
                                        style={{
                                            width: '100%',
                                            height: '100%'
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Name */}
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma.white[80],
                                letterSpacing: '-0.48px',
                                textAlign: 'center',
                                lineHeight: 'normal',
                            }}>
                                {formData.name}
                            </Typography>
                        </Box>
                    </CommonCard>

                    {/* Point Card - grade < 50일 때만 표시 */}
                    {meInfo?.grade < 50 && (
                        <CommonCard sx={{
                            justifyContent: 'space-between',
                        }}>
                            {/* Settings Icon */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                width: '100%',
                                gap: '5px'
                            }}>
                                <Box sx={{
                                    width: '20px',
                                    height: '20px',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}>
                                    <Box sx={{
                                        position: 'absolute',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '6px',
                                        height: '12px'
                                    }}
                                        onClick={() => {
                                            navigate('/rewards/history', {
                                                state: { rewardHistoryFromMypage: true },
                                            });
                                        }}>
                                        <Next />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Point Display */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                width: '100%',
                                gap: '5px'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                }}>
                                    {/* Point Icon */}
                                    <Point />

                                    {/* Point Value */}
                                    <Typography sx={{
                                        fontSize: '25px',
                                        fontWeight: 700,
                                        letterSpacing: '-0.75px',
                                        lineHeight: 'normal',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {formData.point ? formData.point.toLocaleString() : '0'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CommonCard>
                    )}
                </Box>

                {/* Level Progress Section - grade < 50일 때만 표시 */}
                {meInfo?.grade < 50 && (
                    <LevelProgressCard
                        level={meInfo?.level || 0}
                        exp={meInfo?.exp || 0}
                        onClick={handleOpenExpDetail}
                    />
                )}

                {/* Didim Integration Info - grade < 50일 때만 표시 */}
                {meInfo?.grade < 50 && (
                    <Box sx={SectionContainerStyle}>
                        <FormLabel
                            text={t('didim_link_info')}
                            sx={{
                                textAlign: 'left'
                            }}
                        />
                        <Box sx={MenuContainerStyle}>
                            {/* Didim Institution */}
                            <MenuItem
                                title={t('didim_organizations_count', { count: membershipsTotal })}
                                onClick={() => {
                                    navigate('/users/membership');
                                }}
                            />
                        </Box>
                        <Box sx={MenuContainerStyle}>
                            <MenuItem
                                title={t('sign_in_with_didim_qr')}
                                onClick={() => {
                                    navigate('/users/qr');
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {/* Customer Service */}
                <Box sx={SectionContainerStyle}>
                    <FormLabel
                        text={t('customer_service')}
                        sx={{
                            textAlign: 'left'
                        }}
                    />
                    <Box sx={MenuContainerStyle}>
                        <MenuItem
                            title={t('notices')}
                            // hasNotification={true}
                            onClick={() => {
                                navigate('/notices/notice-list');
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={SectionContainerStyle}>
                    <FormLabel
                        text={t('settings_and_account')}
                        sx={{
                            textAlign: 'left'
                        }}
                    />
                    <Box sx={MenuContainerStyle}>
                        <MenuItem
                            title={t('language_settings')}
                            onClick={() => navigate('/users/language')}
                        />
                    </Box>
                    <Box sx={MenuContainerStyle}>
                        <MenuItem
                            title={t('push_notifications')}
                            onClick={() => navigate('/users/push')}
                        />
                    </Box>
                    <Box sx={MenuContainerStyle}>
                        <CommonCard sx={{
                            padding: '5px 15px',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                        }} >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0px'
                            }}>

                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.palette.figma.white[80],
                                    letterSpacing: '-0.42px'
                                }}>
                                    {t('app_version')}
                                </Typography>
                            </Box>
                            <Typography sx={{
                                color: theme.palette.figma.white[60],
                                textAlign: 'right',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: 'normal',
                                letterSpacing: '-0.42px'
                            }}>
                                {displayVersion || '-'}
                            </Typography>

                        </CommonCard>
                    </Box>
                    {meInfo?.grade < 50 &&
                        <>
                            <Box sx={MenuContainerStyle}>
                                <MenuItem
                                    title={t('terms_of_service')}
                                    onClick={() => navigate('/auth/terms', {
                                        state: { type: 'terms_of_service', prev: '/users/mypage' },
                                    })}
                                />
                            </Box>
                            <Box sx={MenuContainerStyle}>
                                <MenuItem
                                    title={t('privacy_policy')}
                                    onClick={() => navigate('/auth/terms', {
                                        state: { type: 'privacy_policy', prev: '/users/mypage' },
                                    })}
                                />
                            </Box>
                            <Box sx={MenuContainerStyle}>
                                <MenuItem
                                    title={t('withdraw_account')}
                                    onClick={() => setDeleteDialogOpen(true)}
                                />
                            </Box>
                        </>
                    }
                    <Box sx={MenuContainerStyle}>
                        <MenuItem
                            title={t('logout')}
                            onClick={handleLogout}
                        />
                    </Box>
                </Box>
            </Box >

            {/* Delete Account Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                disableEnforceFocus={false}
                disableAutoFocus={false}
                disableRestoreFocus={false}
                sx={{
                    '& .MuiDialog-paper': {
                        margin: 0,
                    },
                    '& .MuiBackdrop-root': {
                        backgroundColor: theme.palette.figma.white[80],
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma.popupBG,
                        display: 'flex',
                        width: '340px',
                        padding: '40px 20px',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '40px',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        display: 'flex',
                        padding: 0,
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        alignSelf: 'stretch',
                        minHeight: 0,
                        gap: '20px',
                    }}
                >
                    <Typography
                        sx={{
                            alignSelf: 'stretch',
                            color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: 'normal',
                            letterSpacing: '-0.48px',
                            textAlign: 'center',
                        }}
                    >
                        {t('delete_account')}
                    </Typography>
                    <Typography
                        sx={{
                            alignSelf: 'stretch',
                            color: theme.palette.figma.white[60] || 'rgba(0, 0, 0, 0.60)',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: 'normal',
                            letterSpacing: '-0.42px',
                            textAlign: 'center',
                        }}
                    >
                        {t('type_delete_account_to_confirm')}
                    </Typography>
                    <InputField
                        fullWidth
                        value={deleteConfirmText}
                        onChange={e => setDeleteConfirmText(e.target.value)}
                        placeholder={t('delete_account')}
                        size="small"
                    />
                </DialogContent>
                <DialogActions
                    disableSpacing
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        p: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            width: '100%'
                        }}
                    >
                        <Button
                            variant="gray"
                            onClick={handleDeleteCancel}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="normal"
                            onClick={handleDeleteConfirm}
                            disabled={
                                !isDeleteConfirmMatched || isLoading
                            }
                        >
                            {isLoading
                                ? t('deleting')
                                : t('delete_account')}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </ContentArea >
    );
};
